/**
 * Pure diagnostic serializer for error boundary payloads.
 *
 * Safely serializes arbitrary thrown values into pretty-printed JSON suitable
 * for display in production error UIs. Handles all edge cases that would
 * otherwise produce `[object Object]` or crash JSON.stringify.
 */

/** Sentinel strings for non-JSON-serializable values. */
const CIRCULAR_REF = "[Circular Reference]";
const BIGINT_PREFIX = "[BigInt] ";
const SYMBOL_PREFIX = "[Symbol] ";
const FUNCTION_PREFIX = "[Function] ";
const UNDEFINED_VALUE = "[undefined]";
const UNREADABLE_GETTER = "[Unreadable getter]";
const INVALID_DATE = "[Invalid Date]";
const MAX_DEPTH_REACHED = "[Maximum depth reached]";
const UNSERIALIZABLE_VALUE = "[Unserializable value]";
const MAX_TRAVERSAL_DEPTH = 12;

/**
 * Recursively converts an arbitrary value into a JSON-safe structure.
 * Never emits `[object Object]` — every object type gets explicit handling.
 *
 * Circular detection is path-based: objects are tracked on the current
 * traversal path (added before descending, removed after). Repeated
 * non-cyclic references are serialized fully; only true cycles are marked.
 */
function safeConvert(value: unknown, path: Set<object>, depth = 0): unknown {
  if (value === null) return null;
  if (value === undefined) return UNDEFINED_VALUE;

  switch (typeof value) {
    case "string":
      return value;
    case "number":
    case "boolean":
      return value;
    case "bigint":
      return `${BIGINT_PREFIX}${value.toString()}`;
    case "symbol":
      return `${SYMBOL_PREFIX}${value.toString()}`;
    case "function":
      return `${FUNCTION_PREFIX}${(value as { name?: string }).name || "anonymous"}`;
    case "object":
      break;
    default:
      return String(value);
  }

  if (depth >= MAX_TRAVERSAL_DEPTH) {
    return MAX_DEPTH_REACHED;
  }

  // Path-based circular reference guard: only true ancestor cycles are flagged.
  // Repeated references that are not on the current path serialize fully.
  if (path.has(value as object)) {
    return CIRCULAR_REF;
  }
  path.add(value as object);

  try {
    // Date — guard against Invalid Date crashing toISOString
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? INVALID_DATE : value.toISOString();
    }

    // Map — represent as ordered array of {key, value} entries so object keys
    // serialize without `[object Object]`.
    if (value instanceof Map) {
      const entries: Array<{ key: unknown; value: unknown }> = [];
      value.forEach((v, k) => {
        entries.push({
          key: safeConvert(k, path, depth + 1),
          value: safeConvert(v, path, depth + 1),
        });
      });
      return { "[Map]": entries };
    }

    // Set
    if (value instanceof Set) {
      const items: unknown[] = [];
      value.forEach((v) => items.push(safeConvert(v, path, depth + 1)));
      return { "[Set]": items };
    }

    // Error — extract non-enumerable properties explicitly
    if (value instanceof Error) {
      return convertError(value, path, depth);
    }

    // Array
    if (Array.isArray(value)) {
      return value.map((item) => safeConvert(item, path, depth + 1));
    }

    // Plain object or other object type
    return convertObject(value as object, path, depth);
  } finally {
    path.delete(value as object);
  }
}

/** Converts an Error instance, extracting non-enumerable name/message/stack and custom props. */
function convertError(error: Error, path: Set<object>, depth: number): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  result.name = error.name;
  result.message = error.message;
  if (error.stack) {
    result.stack = error.stack;
  }

  // Error.cause (ES2022)
  if ("cause" in error && error.cause !== undefined) {
    result.cause = safeConvert(error.cause, path, depth + 1);
  }

  // Enumerate ALL own properties including symbol-keyed via Reflect.ownKeys
  const allKeys = Reflect.ownKeys(error);
  for (const key of allKeys) {
    // Skip already-handled standard Error fields
    if (key === "name" || key === "message" || key === "stack" || key === "cause") {
      continue;
    }
    const serializedKey =
      typeof key === "symbol" ? `${SYMBOL_PREFIX}${key.toString()}` : String(key);
    result[serializedKey] = safeReadProperty(error, key, path, depth + 1);
  }

  return result;
}

/** Safely reads a property using the descriptor, handling getters that may throw. */
function safeReadProperty(
  obj: object,
  key: string | symbol,
  path: Set<object>,
  depth: number
): unknown {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(obj, key);
    if (descriptor && "get" in descriptor && !("value" in descriptor)) {
      const getter = descriptor.get;
      if (typeof getter !== "function") return UNDEFINED_VALUE;
      // It's a getter — invoke cautiously
      try {
        const value = getter.call(obj);
        return safeConvert(value, path, depth);
      } catch {
        return UNREADABLE_GETTER;
      }
    }
    // Value property or simple descriptor
    const value = descriptor ? descriptor.value : undefined;
    return safeConvert(value, path, depth);
  } catch {
    return UNREADABLE_GETTER;
  }
}

/** Converts a plain object, safely reading each own property (including symbol keys). */
function convertObject(obj: object, path: Set<object>, depth: number): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const allKeys = Reflect.ownKeys(obj);
  for (const key of allKeys) {
    const serializedKey =
      typeof key === "symbol" ? `${SYMBOL_PREFIX}${key.toString()}` : String(key);
    result[serializedKey] = safeReadProperty(obj, key, path, depth + 1);
  }
  return result;
}

/**
 * Attempts to parse a string as JSON. Returns the parsed object on success,
 * or undefined if the string is not valid JSON.
 */
function tryParseJson(str: string): unknown | undefined {
  if (typeof str !== "string") return undefined;
  const trimmed = str.trim();
  // Quick check: must look like JSON object or array
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** Diagnostic payload shape for the error boundary UI. */
export interface DiagnosticPayload {
  capturedAt: string;
  pageUrl: string;
  userAgent: string;
  thrownValue: unknown;
  parsedJsonMessage?: unknown;
  componentStack: string | null;
}

/**
 * Normalizes a thrown value (Error or anything else) into a serializable form.
 * If the Error.message or thrown string is valid JSON, includes a parsed form.
 */
export function normalizeThrownValue(thrown: unknown): {
  normalized: unknown;
  parsedJson?: unknown;
} {
  try {
    const path = new Set<object>();
    const normalized = safeConvert(thrown, path);

    // Check if message content is valid JSON
    let rawJsonString: string | undefined;
    if (thrown instanceof Error) {
      rawJsonString = thrown.message;
    } else if (typeof thrown === "string") {
      rawJsonString = thrown;
    }

    let parsedJson: unknown | undefined;
    if (rawJsonString) {
      const parsed = tryParseJson(rawJsonString);
      if (parsed !== undefined) {
        // Re-serialize the parsed form through our safe converter for display
        const freshPath = new Set<object>();
        parsedJson = safeConvert(parsed, freshPath);
      }
    }

    return { normalized, parsedJson };
  } catch {
    return { normalized: UNSERIALIZABLE_VALUE };
  }
}

/**
 * Builds the full diagnostic payload for display in the error boundary.
 */
export function buildDiagnosticPayload(
  thrown: unknown,
  componentStack: string | null,
  capturedAt: string
): DiagnosticPayload {
  const { normalized, parsedJson } = normalizeThrownValue(thrown);

  const payload: DiagnosticPayload = {
    capturedAt,
    pageUrl: typeof window !== "undefined" ? window.location.href : "unknown",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
    thrownValue: normalized,
    componentStack: componentStack || null,
  };

  if (parsedJson !== undefined) {
    payload.parsedJsonMessage = parsedJson;
  }

  return payload;
}

/**
 * Serializes a DiagnosticPayload to a pretty-printed JSON string.
 * This is the canonical display string for the error boundary UI and copy button.
 */
export function serializeDiagnosticPayload(payload: DiagnosticPayload): string {
  return JSON.stringify(payload, null, 2);
}

/**
 * Safely converts an arbitrary unknown value to a string representation
 * suitable for diagnostic messages. Never emits `[object Object]`.
 */
export function safeStringify(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return value;
  try {
    if (value instanceof Error) return value.message;
    const path = new Set<object>();
    const normalized = safeConvert(value, path);
    return JSON.stringify(normalized);
  } catch {
    return UNSERIALIZABLE_VALUE;
  }
}
